import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
} from '@mui/material';
import {
    Email,
    ArrowBack,
} from '@mui/icons-material';
import PageContainer from '../components/layout/PageContainer';
import { forgotPassword } from '../services/authService';

const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        // Basic validation
        if (!email) {
            setError('Email is required.');
            setIsLoading(false);
            return;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await forgotPassword(email);
            setSuccess(response.message);
            setEmail(''); // Clear the form
            
            // Redirect to login page after 3 seconds
            setTimeout(() => {
                navigate('/login', { 
                    state: { 
                        message: 'Check your email for the temporary password and log in.' 
                    } 
                });
            }, 3000);
            
        } catch (err: any) {
            setError(err.message || 'Password reset failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Card sx={{ maxWidth: 400, width: '100%' }}>
                    <CardContent sx={{ p: 4 }}>
                        {/* Header */}
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                mb: 1,
                                fontWeight: 'bold',
                            }}
                        >
                            Forgot Password
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', mb: 3 }}
                        >
                            Enter your email address and we'll send you a new temporary password
                        </Typography>

                        {/* Success Message */}
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {/* Form */}
                        <Box component="form" onSubmit={handleSubmit}>
                            {/* Email Field */}
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Error Alert */}
                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isLoading ? 'Sending...' : 'Reset Password'}
                            </Button>

                            {/* Back to Login Link */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    component={Link}
                                    to="/login"
                                    startIcon={<ArrowBack />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        </Box>

                        {/* Additional Information */}
                        {!success && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Note:</strong> Password reset is only available for approved accounts. 
                                    If your account is pending approval, please wait for admin approval first.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </PageContainer>
    );
};

export default ForgotPassword;